import type { ManualQuotation } from '../../shared/contract/quotations.js';

export interface ManualQuoteDraftLine {
  lineItemId?: string;
  description?: string;
  unitPrice: string;
  quantityOffered: string;
  total: string;
}

export interface ManualQuoteDraft {
  vendorName: string;
  currency: string;
  priceBasis?: string;
  grandTotal: string;
  taxAmount: string;
  freightAmount: string;
  discountAmount: string;
  taxBasis: string;
  lines: ManualQuoteDraftLine[];
}

type BuildResult = { ok: true; value: ManualQuotation } | { ok: false; error: string };

/** Preserve blank as absent and zero as an explicit amount; reject invalid money before the API call. */
export function buildManualQuotationInput(draft: ManualQuoteDraft): BuildResult {
  const amount = (raw: string, label: string): number | undefined | string => {
    if (raw.trim() === '') return undefined;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : `Enter a valid nonnegative ${label}.`;
  };
  const grandTotal = amount(draft.grandTotal, 'grand total');
  if (typeof grandTotal === 'string') return { ok: false, error: grandTotal };

  const adjustments: NonNullable<ManualQuotation['adjustments']> = [];
  for (const [kind, label, raw] of [
    ['tax', 'Tax', draft.taxAmount],
    ['freight', 'Freight', draft.freightAmount],
    ['discount', 'Discount', draft.discountAmount],
  ] as const) {
    const value = amount(raw, label.toLowerCase());
    if (typeof value === 'string') return { ok: false, error: value };
    if (value !== undefined) adjustments.push({ kind, label, amount: value });
  }

  const lines: ManualQuotation['lines'] = [];
  for (const row of draft.lines) {
    const description = row.description?.trim() ?? '';
    const unitPrice = amount(row.unitPrice, 'unit price');
    const quantityOffered = amount(row.quantityOffered, 'quantity offered');
    const total = amount(row.total, 'line total');
    if (typeof unitPrice === 'string') return { ok: false, error: unitPrice };
    if (typeof quantityOffered === 'string') return { ok: false, error: quantityOffered };
    if (typeof total === 'string') return { ok: false, error: total };
    if (description === '' && row.lineItemId === undefined) {
      if (unitPrice !== undefined || quantityOffered !== undefined || total !== undefined)
        return { ok: false, error: 'Enter a description for each extra quoted item.' };
      continue;
    }
    if (row.lineItemId !== undefined && unitPrice === undefined && quantityOffered === undefined && total === undefined)
      continue;
    lines.push({
      ...(row.lineItemId !== undefined ? { lineItemId: row.lineItemId } : {}),
      ...(description !== '' ? { description } : {}),
      ...(unitPrice !== undefined ? { unitPrice } : {}),
      ...(quantityOffered !== undefined ? { quantityOffered } : {}),
      ...(total !== undefined ? { total } : {}),
    });
  }

  if (!['unknown', 'included', 'excluded'].includes(draft.taxBasis))
    return { ok: false, error: 'Choose whether item prices include tax.' };
  return {
    ok: true,
    value: {
      vendorName: draft.vendorName,
      currency: draft.currency,
      ...(draft.priceBasis !== undefined ? { priceBasis: draft.priceBasis } : {}),
      ...(grandTotal !== undefined ? { grandTotal } : {}),
      ...(adjustments.length > 0 ? { adjustments } : {}),
      ...(draft.taxBasis === 'unknown' ? {} : { linePricesIncludeTax: draft.taxBasis === 'included' }),
      lines,
    },
  };
}
